import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'

interface ReportPayload {
  name?: string
  description?: string
  type?: string
  [key: string]: any
}

interface ReportTemplatePayload {
  name?: string
  description?: string
  content?: any
  [key: string]: any
}

interface GenerateReportPayload {
  template_id?: number | string
  project_id?: number | string
  [key: string]: any
}

interface QueryParams {
  [key: string]: string | number | boolean
}

/**
 * Updates a report
 * @param id - Report ID
 * @param payload - Report data to update
 * @returns API response
 */
export const patchReport = (
  id: number | string,
  payload: ReportPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.REPORTS.PATCH_REPORT(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Alias for patchReport - for backward compatibility
 */
export const editReport = patchReport

/**
 * Deletes a report
 * @param id - Report ID
 * @returns API response
 */
export const deleteReport = (id: number | string): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.GOALS.DELETE_REPORT(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Creates a new report for a project
 * @param orgPk - Organization ID
 * @param projectId - Project ID
 * @param payload - Report data
 * @returns API response
 */
export const postReportsList = (
  orgPk: number | string,
  projectId: number | string,
  payload: ReportPayload
): Promise<ApiClientResult> =>
  client
    .post(API_URL.REPORTS.POST_REPORTS_LIST(orgPk, projectId), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Generates a preview of a report
 * @param orgPk - Organization ID
 * @param proposalId - Proposal ID
 * @returns API response with file data
 */
export const postReportPreview = (
  orgPk: number | string,
  proposalId: number | string
): Promise<ApiClientResult> =>
  client
    .post(
      API_URL.REPORTS.POST_REPORTS_PREVIEW(orgPk, proposalId),
      {},
      { responseType: 'arraybuffer' }
    )
    .then(response => response)
    .catch(error => error.response)

/**
 * Generates a preview of a report by query
 * @param payload - Query parameters
 * @returns API response with file data
 */
export const postReportPreviewByQuery = (
  payload: QueryParams
): Promise<ApiClientResult> =>
  client
    .post(API_URL.REPORTS.POST_REPORTS_PREVIEW_BY_QUERY, payload, {
      responseType: 'arraybuffer'
    })
    .then(response => response)
    .catch(error => error.response)

/**
 * Generates a preview of an organization report by query string
 * @param orgPk - Organization ID
 * @param query - Query string
 * @returns API response with file data
 */
export const postReportPreviewOrg = (
  orgPk: number | string,
  query: string
): Promise<ApiClientResult> => {
  const parseQueryString = (queryStr: string): QueryParams => {
    return queryStr.split('&').reduce((acc: QueryParams, pair) => {
      const [key, value] = pair.split('=')
      acc[key] = value
      return acc
    }, {})
  }
  const queryObject = parseQueryString(query)
  return client
    .post(
      API_URL.REPORTS.POST_REPORTS_PREVIEW_BY_QUERY + `?${query}`,
      queryObject,
      { responseType: 'arraybuffer' }
    )
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Generates a report
 * @param orgPk - Organization ID
 * @param payload - Generation parameters
 * @returns API response
 */
export const postGenerateReport = (
  orgPk: number | string,
  payload: GenerateReportPayload
): Promise<ApiClientResult> =>
  client
    .post(API_URL.REPORTS.POST_GENERATE_REPORT(orgPk), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Updates a report proposal
 * @param proposalId - Proposal ID
 * @param payload - Proposal data
 * @returns API response
 */
export const patchReportProposal = (
  proposalId: number | string,
  payload: ReportPayload
): Promise<ApiClientResult> =>
  client
    .patch(API_URL.REPORTS.GET_REPORTS(proposalId), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Deletes a report proposal
 * @param proposalId - Proposal ID
 * @returns API response
 */
export const deleteReportProposal = (
  proposalId: number | string
): Promise<ApiClientResult> =>
  client
    .delete(API_URL.REPORTS.GET_REPORTS(proposalId))
    .then(response => response)
    .catch(error => error.response)

/* Templates */
/**
 * Creates a new report template
 * @param teamId - Team ID
 * @param payload - Template data
 * @returns API response
 */
export const postReportTemplates = (
  teamId: number | string,
  payload: ReportTemplatePayload
): Promise<ApiClientResult> =>
  client
    .post(API_URL.REPORTS.POST_REPORTS_TEMPLATES_LIST(teamId), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Duplicates a report template
 * @param id - Template ID
 * @returns API response
 */
export const postReportTemplatesDuplicate = (
  id: number | string
): Promise<ApiClientResult> =>
  client
    .post(API_URL.REPORTS.POST_REPORTS_TEMPLATES_DUPLICATE(id))
    .then(response => response)
    .catch(error => error.response)

/**
 * Generates a preview of a report template
 * @param orgPk - Organization ID
 * @param templateId - Template ID
 * @returns API response with file data
 */
export const postReportPreviewTemplate = (
  orgPk: number | string,
  templateId: number | string
): Promise<ApiClientResult> =>
  client
    .post(
      API_URL.REPORTS.POST_REPORTS_PREVIEW_TEMPLATE(orgPk, templateId),
      {},
      { responseType: 'arraybuffer' }
    )
    .then(response => response)
    .catch(error => error.response)

/**
 * Updates a report template
 * @param id - Template ID
 * @param payload - Template data
 * @returns API response
 */
export const patchReportTemplate = (
  id: number | string,
  payload: ReportTemplatePayload
): Promise<ApiClientResult> =>
  client
    .patch(API_URL.REPORTS.PATCH_REPORT_TEMPLATE(id), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Deletes a report template
 * @param id - Template ID
 * @returns API response
 */
export const deleteReportTemplate = (
  id: number | string
): Promise<ApiClientResult> =>
  client
    .delete(API_URL.REPORTS.DELETE_REPORT_TEMPLATE(id))
    .then(response => response)
    .catch(error => error.response)

/**
 * Downloads an Excel file
 * @param url - File URL
 * @param org - Organization ID
 * @param name - File name
 * @returns Promise resolving with response or 'no_data' if file empty
 */
export const downloadXls = (
  url: string,
  org: string | number,
  name: string
): Promise<ApiResponse | string> => {
  return client
    .get(url + '&org=' + org, {
      responseType: 'arraybuffer'
    })
    .then(response => {
      if (response.data && response.data.byteLength) {
        const fileUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = fileUrl
        link.setAttribute('download', `${name}.xlsx`)
        document.body.appendChild(link)
        link.click()
        return response
      } else {
        return 'no_data'
      }
    })
    .catch(error => error.response)
}

/**
 * Queries the explorer API
 * @param payload - Query parameters
 * @returns API response
 */
export const getExplorer = async (
  payload: QueryParams
): Promise<ApiClientResult> => {
  try {
    const response = await client.get(API_URL.REPORTS.EXPLORER, {
      params: payload
    })
    return response
  } catch (error) {
    return error.response
  }
}
